import { useEffect, useRef, useState } from 'react';
import { AppIcon } from './app-shell-core';

export type BrowserNotificationItem = {
  id: string;
  type?: string | null;
  title?: string | null;
  body?: string | null;
  link?: string | null;
  createdAt: string;
  readAt?: string | null;
};

type BrowserAlertControlProps = {
  storageKey: string;
  brandName: string;
  poll: () => Promise<{ notifications?: BrowserNotificationItem[] }>;
  onOpen: (link: string) => void;
  className?: string;
};

function browserAlertCopy(item: BrowserNotificationItem): string {
  const type = String(item.type || '').toUpperCase();
  const link = String(item.link || '').toLowerCase();
  if (type === 'APPOINTMENT' || link.includes('/appointments') || link.includes('/calendar')) return 'An appointment update needs your attention.';
  if (type === 'MESSAGE' || link.includes('/communications') || link.includes('/messages')) return 'You have a new secure message or communication update.';
  if (type === 'FORM' || link.includes('/forms')) return 'A form or document update needs your attention.';
  if (type === 'BILLING' || link.includes('/billing') || link.includes('/payments')) return 'A billing update is available.';
  return 'There is a new secure Nuramend alert to review.';
}

function newestCreatedAt(items: BrowserNotificationItem[]): string {
  return items.reduce((latest, item) => {
    const value = item.createdAt || '';
    return value > latest ? value : latest;
  }, '');
}

export function BrowserAlertControl({ storageKey, brandName, poll, onOpen, className = '' }: BrowserAlertControlProps) {
  const supported = typeof window !== 'undefined' && window.isSecureContext && 'Notification' in window;
  const enabledKey = `${storageKey}:enabled`;
  const cursorKey = `${storageKey}:cursor`;
  const pollRef = useRef(poll);
  const openRef = useRef(onOpen);
  pollRef.current = poll;
  openRef.current = onOpen;

  const [enabled, setEnabled] = useState(() => supported && Notification.permission === 'granted' && localStorage.getItem(enabledKey) === '1');
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(() => supported ? Notification.permission : 'unsupported');
  const [busy, setBusy] = useState(false);

  const establishBaseline = async () => {
    try {
      const result = await pollRef.current();
      const items = Array.isArray(result?.notifications) ? result.notifications : [];
      const newest = newestCreatedAt(items);
      localStorage.setItem(cursorKey, newest || new Date().toISOString());
    } catch {
      localStorage.setItem(cursorKey, new Date().toISOString());
      // Browser alerts are optional and should never interrupt the clinical application.
    }
  };

  useEffect(() => {
    if (!supported) {
      setPermission('unsupported');
      setEnabled(false);
      return;
    }
    const nextPermission = Notification.permission;
    setPermission(nextPermission);
    setEnabled(nextPermission === 'granted' && localStorage.getItem(enabledKey) === '1');
  }, [enabledKey, supported]);

  useEffect(() => {
    if (!supported || !enabled || Notification.permission !== 'granted') return;
    let active = true;

    const check = async () => {
      try {
        const result = await pollRef.current();
        if (!active) return;
        const items = Array.isArray(result?.notifications) ? result.notifications : [];
        const newest = newestCreatedAt(items);
        const cursor = localStorage.getItem(cursorKey) || '';
        if (!cursor) {
          localStorage.setItem(cursorKey, newest || new Date().toISOString());
          return;
        }
        const fresh = items
          .filter((item) => item.createdAt > cursor)
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
          .slice(-4);
        for (const item of fresh) {
          const notification = new Notification(brandName, {
            body: browserAlertCopy(item),
            tag: `nuramend-${storageKey}-${item.id}`,
            silent: false,
          });
          notification.onclick = () => {
            window.focus();
            if (item.link) openRef.current(item.link);
            notification.close();
          };
        }
        if (newest) localStorage.setItem(cursorKey, newest);
      } catch {
        // Poll failures are intentionally silent. Existing in-app/email notification channels remain authoritative.
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), 30_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [enabled, storageKey, supported, brandName, cursorKey]);

  const toggle = async () => {
    if (!supported || busy) return;
    if (enabled) {
      localStorage.setItem(enabledKey, '0');
      setEnabled(false);
      return;
    }
    if (Notification.permission === 'denied') {
      setPermission('denied');
      return;
    }
    setBusy(true);
    try {
      const next = Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();
      setPermission(next);
      if (next === 'granted') {
        await establishBaseline();
        localStorage.setItem(enabledKey, '1');
        setEnabled(true);
      }
    } finally {
      setBusy(false);
    }
  };

  const state = !supported ? 'unsupported' : permission === 'denied' ? 'blocked' : enabled ? 'enabled' : 'disabled';
  const title = state === 'enabled'
    ? 'Browser alerts are on. Click to turn them off.'
    : state === 'blocked'
      ? 'Browser alerts are blocked by this browser. Update site notification permissions to enable them.'
      : state === 'unsupported'
        ? 'Browser alerts require a supported browser and a secure HTTPS connection.'
        : 'Enable private browser alerts for new Nuramend notifications.';

  return <button
    type="button"
    className={`icon-button browser-alert-control state-${state} ${className}`.trim()}
    onClick={() => void toggle()}
    disabled={!supported || busy}
    aria-label={title}
    aria-pressed={enabled}
    title={title}
  >
    <AppIcon name="bell" />
    <span className="browser-alert-state-dot" aria-hidden="true" />
  </button>;
}
